/**
 * Where the project's delivery to Mia-Platform Console stands, live (hooks/useConsoleDeliveries.js): the state in
 * words with one more line (progress, what comes next, or why it failed), four steps (Received · Review · Images ·
 * Dev), the version each environment runs, and the links: Review in Console (while a review is pending) or Open in
 * Console (the project page), and Open app in dev once it runs there. Shown in the Publish dialog after "Send to
 * Console" and later, and on the project Home. Nothing at all for a project never delivered (or without Console Next).
 */
import React from 'react';
import PropTypes from 'prop-types';
import { ArrowSquareOut } from '@phosphor-icons/react';
import useConsoleDelivery from '../hooks/useConsoleDeliveries';
import { consoleWebUrl } from '../services/ConsoleNextService';
import './ConsoleDeliveryStatus.css';

const STEPS = [
  { key: 'received', label: 'Received' },
  { key: 'review', label: 'Review' },
  { key: 'build', label: 'Images' },
  { key: 'deploy', label: 'Dev' },
];

// How far each state got: the index of the current step (everything before it is done).
const CURRENT = { received: 0, review: 1, building: 2, ready: 3, deploying: 3, deployed: 4 };

/** `[{ key, label, state: done | current | next | failed }]` for a status. */
export function deliverySteps(status) {
  if (!status) { return []; }
  if (status.state === 'failed') {
    const at = status.failedStep === 'deploy' ? 3 : 2;
    return STEPS.map((step, i) => ({ ...step, state: i < at ? 'done' : i === at ? 'failed' : 'next' }));
  }
  const at = CURRENT[status.state] ?? 0;
  // "ready": built and reviewed, dev is next but nothing runs it yet.
  return STEPS.map((step, i) => ({ ...step, state: i < at ? 'done' : i === at && status.state !== 'ready' ? 'current' : 'next' }));
}

const TONE = { deployed: 'ok', failed: 'bad', review: 'warn', ready: 'info' };

/** dev / staging / prod, in that order, with the version each runs ("—" when none or no such environment). */
export function stageVersions(stages) {
  return ['dev', 'staging', 'prod'].map((stage) => {
    const env = (stages || []).find((s) => s.stage === stage);
    return { stage, version: env?.version || null, present: Boolean(env), url: env?.url || null };
  });
}

export function ConsoleDeliveryCard({ status, compact = false, actions = true }) {
  const tone = TONE[status.state] || 'progress';
  const reviewing = status.state === 'review' && status.reviewPath;
  const openUrl = consoleWebUrl(status.openPath || status.projectPath);
  const stages = stageVersions(status.stages);
  return (
    <section aria-label="Delivery to Mia-Platform Console" className={`cds cds-${tone}${compact ? ' cds-compact' : ''}`} data-testid="console-delivery-status">
      <div className="cds-head">
        <span aria-hidden className={`cds-dot cds-dot-${tone}`} />
        <span className="cds-title" role="status">{status.label}</span>
        <span className="cds-muted">{status.title} {status.version ? `· ${status.version}` : ''}</span>
      </div>
      {status.state === 'failed' && status.reason
        ? <p className="cds-reason" role="alert">{status.reason}</p>
        : status.detail && <p className="cds-muted cds-detail">{status.detail}</p>}
      <ol className="cds-steps">
        {deliverySteps(status).map((step) => (
          <li className={`cds-step is-${step.state}`} key={step.key}><span aria-hidden className="cds-step-dot" />{step.label}<span className="cds-sr">{` (${step.state})`}</span></li>
        ))}
      </ol>
      <div className="cds-stages" data-testid="console-delivery-stages">
        {stages.map((s) => {
          const body = (
            <>
              <span className="cds-stage-name">{s.stage}</span>
              <span className="cds-stage-version">{s.version || '—'}</span>
              {s.url && <ArrowSquareOut aria-hidden className="cds-stage-open" size={12} />}
            </>
          );
          const className = `cds-stage${s.version ? ' has-version' : ''}${s.url ? ' has-url' : ''}`;
          // A stage with an address is a link that opens the app there (dev, staging, prod: each its own).
          return s.url
            ? <a aria-label={`Open ${s.stage}${s.version ? ` ${s.version}` : ''}`} className={className} href={s.url} key={s.stage} rel="noopener noreferrer" target="_blank" title={`Open ${s.url}`}>{body}</a>
            : <span className={className} key={s.stage} title={s.present ? undefined : `No ${s.stage} environment yet`}>{body}</span>;
        })}
      </div>
      {actions && <div className="cds-actions">
        {status.state === 'deployed' && status.devUrl && !stages.some((s) => s.url) && (
          <a className="cds-btn" href={status.devUrl} rel="noopener noreferrer" target="_blank">Open app in dev</a>
        )}
        <a className={`cds-btn${reviewing ? ' cds-btn-primary' : ''}`} href={openUrl} rel="noopener noreferrer" target="_blank">
          {reviewing ? 'Review in Console' : 'Open in Console'}
        </a>
      </div>}
    </section>
  );
}

ConsoleDeliveryCard.propTypes = {
  actions: PropTypes.bool,
  compact: PropTypes.bool,
  status: PropTypes.shape({
    detail: PropTypes.string,
    devUrl: PropTypes.string,
    failedStep: PropTypes.string,
    label: PropTypes.string,
    openPath: PropTypes.string,
    projectPath: PropTypes.string,
    reason: PropTypes.string,
    reviewPath: PropTypes.string,
    stages: PropTypes.array,
    state: PropTypes.string.isRequired,
    title: PropTypes.string,
    version: PropTypes.string,
  }).isRequired,
};

/**
 * The live card for `projectId`. Until Console Next answers, `fallback` (e.g. the Publish dialog's "Delivered" line)
 * or nothing.
 */
export default function ConsoleDeliveryStatus({ projectId, compact = false, fallback = null }) {
  const status = useConsoleDelivery(projectId);
  if (!status) { return fallback; }
  return <ConsoleDeliveryCard compact={compact} status={status} />;
}

ConsoleDeliveryStatus.propTypes = {
  compact: PropTypes.bool,
  fallback: PropTypes.node,
  projectId: PropTypes.string,
};
